import { Component, Suspense, type ReactNode } from "react";

interface ChunkBoundaryProps {
  children: ReactNode;
  /** Told once when the part could not be fetched or drawn. */
  onError?: ((error: Error) => void) | undefined;
}

/**
 * Around a part of the editor that is fetched on demand. A chunk that does not arrive (offline, or
 * gone after a deploy) makes `lazy` throw while rendering; without a boundary here that takes the
 * host's page down with it. The editor goes on without the part, and the host hears why.
 */
export class ChunkBoundary extends Component<ChunkBoundaryProps, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    this.props.onError?.(error instanceof Error ? error : new Error(String(error)));
  }

  override render(): ReactNode {
    if (this.state.failed) return null;
    return <Suspense fallback={null}>{this.props.children}</Suspense>;
  }
}
